import { PlatformError } from './adapter.js';
import { computeRoundPlan } from './diff.js';
import { confirmBinding, rebind } from './identity.js';
import { importSchedule } from './import.js';
import { makeScheduleVersion, nextRound, roundsInOrder } from './schedule.js';
import { MAIN } from './types.js';
import { hasBlockingErrors, validateSchedule } from './validate.js';
export function newEventRecord(eventId, title, timezone = 'Europe/London') {
    return {
        eventId,
        title,
        timezone,
        state: 'draft',
        scheduleVersion: null,
        validation: [],
        importErrors: [],
        roomMap: {},
        bindings: [],
        overrides: [],
        excluded: [],
        absent: [],
        currentRoundId: null,
        activeTransition: null,
        transitions: [],
        statuses: {},
        manualObservations: [],
        roundTimes: {},
        lastReconciledAt: null,
        lastTransitionAt: null,
        audit: [],
    };
}
export class EngineError extends Error {
    code;
    constructor(message, code) {
        super(message);
        this.code = code;
        this.name = 'EngineError';
    }
}
export const DEFAULT_BREAKOUT_SETTINGS = {
    autoMoveIntoRooms: true,
    autoMoveIntoMain: true,
    allowSelfSelect: false,
    allowReturnToMain: false,
    closeCountdownSeconds: 10,
};
/**
 * The rotation engine: owns the event record, drives the adapter, and
 * enforces the lifecycle in spec section 10. It is deliberately a plain
 * class over a JSON record so the backend can persist the record after
 * every step and a reloaded controller can resume from it.
 */
export class RotationEngine {
    adapter;
    opts;
    record;
    now;
    maxWriteAttempts;
    paceMs;
    sleep;
    settings;
    /** Last live read, kept for candidate confirmation. */
    lastLive = [];
    constructor(record, adapter, opts) {
        this.adapter = adapter;
        this.opts = opts;
        this.record = record;
        this.now = opts.now ?? (() => new Date());
        this.maxWriteAttempts = opts.maxWriteAttempts ?? 3;
        this.paceMs = opts.paceMs ?? 150;
        this.sleep = opts.sleep ?? ((ms) => new Promise((r) => setTimeout(r, ms)));
        this.settings = { ...DEFAULT_BREAKOUT_SETTINGS, ...(opts.breakoutSettings ?? {}) };
    }
    // ---- setup ------------------------------------------------------------
    import(files) {
        this.assertState(['draft', 'validated', 'ready'], 'import');
        const { schedule, errors } = importSchedule(files);
        this.record.importErrors = errors;
        if (errors.length) {
            this.record.validation = [];
            this.record.state = 'draft';
            this.audit('import', 'failed', `${errors.length} row error(s)`);
            return { errors, findings: [] };
        }
        const findings = validateSchedule(schedule);
        this.record.validation = findings;
        const version = (this.record.scheduleVersion?.version ?? 0) + 1;
        this.record.scheduleVersion = makeScheduleVersion(schedule, version, this.now);
        this.record.state = hasBlockingErrors(findings) ? 'draft' : 'validated';
        this.audit('import', hasBlockingErrors(findings) ? 'failed' : 'ok', `schedule v${version} ${this.record.scheduleVersion.hash}`);
        return { errors, findings };
    }
    /**
     * FR04: map logical rooms to platform rooms. Existing rooms are reused only
     * when `reuseByName` is true; missing rooms are created; nothing is renamed
     * or deleted. Rooms are never recreated after the first round.
     */
    async mapRooms(opts) {
        this.assertState(['validated', 'ready'], 'mapRooms');
        const schedule = this.schedule();
        const listing = await this.adapter.listRooms();
        const byName = new Map(listing.rooms.map((r) => [r.name, r.platformRoomId]));
        const toCreate = [];
        for (const room of schedule.rooms) {
            const existing = this.record.roomMap[room.roomId];
            if (existing && listing.rooms.some((r) => r.platformRoomId === existing))
                continue;
            const reuse = opts.reuseByName ? byName.get(room.roomName) : undefined;
            if (reuse)
                this.record.roomMap[room.roomId] = reuse;
            else
                toCreate.push({ roomId: room.roomId, name: room.roomName });
        }
        if (toCreate.length) {
            const created = await this.adapter.createRooms(toCreate.map((r) => r.name));
            for (const c of toCreate) {
                const match = created.find((x) => x.name === c.name);
                if (!match)
                    throw new EngineError(`Platform did not return a room named ${c.name}`, 'room_create_mismatch');
                this.record.roomMap[c.roomId] = match.platformRoomId;
            }
        }
        const applied = await this.adapter.configureRooms(this.settings);
        this.audit('mapRooms', applied.unsupported.length ? 'partial' : 'ok', applied.unsupported.length ? `unsupported settings: ${applied.unsupported.join(', ')}` : undefined);
        return this.record.roomMap;
    }
    /** FR06 readiness: what still blocks launch. */
    readiness() {
        const problems = [];
        if (!this.record.scheduleVersion)
            problems.push('No schedule imported');
        if (hasBlockingErrors(this.record.validation))
            problems.push('Schedule has validation errors');
        if (this.record.scheduleVersion) {
            for (const room of this.record.scheduleVersion.schedule.rooms)
                if (!this.record.roomMap[room.roomId])
                    problems.push(`Room ${room.roomName} not mapped`);
            const bound = new Set(this.record.bindings.filter((b) => !b.stale).map((b) => b.participantId));
            const excluded = new Set(this.record.excluded);
            for (const p of this.record.scheduleVersion.schedule.participants) {
                if (excluded.has(p.participantId))
                    continue;
                if (!bound.has(p.participantId) && !this.record.absent.includes(p.participantId))
                    problems.push(`${p.role} ${p.displayName} not matched or marked absent`);
            }
        }
        if (!this.record.lastReconciledAt)
            problems.push('Live attendees not yet reconciled');
        return { ready: problems.length === 0, problems };
    }
    markReady() {
        this.assertState(['validated', 'ready'], 'markReady');
        const r = this.readiness();
        if (!r.ready)
            throw new EngineError(`Not ready: ${r.problems.join('; ')}`, 'not_ready');
        this.record.state = 'ready';
        this.audit('markReady', 'ok');
    }
    // ---- identity ---------------------------------------------------------
    /**
     * FR05/FR12: read the live participant list and rebuild bindings. Only
     * meaningful as a checkpoint when the rooms are closed; while a round is
     * active it reads whatever is visible from the operator's location and
     * does not change the absent set.
     */
    async reconcile() {
        const schedule = this.schedule();
        const live = await this.adapter.listParticipants();
        this.lastLive = live;
        const lastRoom = new Map();
        if (this.record.currentRoundId) {
            for (const [pid, room] of this.destinationsFor(this.record.currentRoundId))
                lastRoom.set(pid, room);
        }
        const result = rebind({ roster: schedule.participants, live, previous: this.record.bindings, lastRoom, now: this.now });
        const atCheckpoint = this.record.state !== 'round_active' && this.record.state !== 'opening' && this.record.state !== 'closing';
        // Keep operator-confirmed bindings whose runtime id is still present; replace the rest.
        const liveIds = new Set(live.map((l) => l.runtimeId));
        const kept = this.record.bindings.filter((b) => liveIds.has(b.runtimeId) && !result.auto.some((a) => a.participantId === b.participantId));
        const keptIds = new Set(kept.map((b) => b.participantId));
        this.record.bindings = [...kept, ...result.auto.filter((a) => !keptIds.has(a.participantId))];
        if (atCheckpoint) {
            const bound = new Set(this.record.bindings.map((b) => b.participantId));
            const excluded = new Set(this.record.excluded);
            this.record.absent = schedule.participants.map((p) => p.participantId).filter((id) => !bound.has(id) && !excluded.has(id));
            this.record.lastReconciledAt = this.now().toISOString();
        }
        this.audit('reconcile', 'ok', `${this.record.bindings.length} bound, ${result.candidates.length} candidate(s), ${this.record.absent.length} absent`);
        return { ...result, live };
    }
    confirmCandidate(candidate) {
        const live = this.lastLive.find((l) => l.runtimeId === candidate.runtimeId);
        if (!live)
            throw new EngineError('Candidate runtime identity no longer present; reconcile again', 'stale_candidate');
        if (this.record.bindings.some((b) => b.runtimeId === candidate.runtimeId && b.participantId !== candidate.participantId)) {
            throw new EngineError('That attendee is already bound to another roster entry', 'runtime_already_bound');
        }
        const b = confirmBinding(candidate, live, this.now);
        this.record.bindings = [...this.record.bindings.filter((x) => x.participantId !== b.participantId), b];
        this.record.absent = this.record.absent.filter((id) => id !== b.participantId);
        this.audit('confirmBinding', 'ok', `${b.participantId} -> ${b.runtimeId}`);
        return b;
    }
    markAbsent(participantId) {
        this.record.bindings = this.record.bindings.filter((b) => b.participantId !== participantId);
        if (!this.record.absent.includes(participantId))
            this.record.absent.push(participantId);
        this.audit('markAbsent', 'ok', participantId);
    }
    // ---- overrides --------------------------------------------------------
    addOverride(o) {
        const full = { ...o, createdBy: this.opts.actor, createdAt: this.now().toISOString() };
        if (o.action.kind === 'exclude' && o.scope === 'remaining_rounds' && !this.record.excluded.includes(o.participantId))
            this.record.excluded.push(o.participantId);
        this.record.overrides.push(full);
        this.audit('override', 'ok', `${o.participantId} ${o.action.kind} ${o.scope}`);
    }
    // ---- rounds -----------------------------------------------------------
    /** FR07/FR08 preview of the round the next transition would open. */
    previewNext() {
        const schedule = this.schedule();
        const target = this.targetRound();
        if (!target)
            return null;
        return computeRoundPlan({
            schedule,
            roundId: target,
            previousRoundId: this.record.currentRoundId,
            overrides: this.record.overrides,
            absent: new Set(this.record.absent),
            excluded: new Set(this.record.excluded),
        });
    }
    /**
     * Idempotent: a repeated call with the same id returns the same record;
     * a different id while one is active is refused (FR18, section 10).
     */
    beginTransition(transitionId, target) {
        const active = this.record.activeTransition;
        if (active) {
            if (active.transitionId === transitionId)
                return active;
            throw new EngineError(`Transition ${active.transitionId} is already in progress`, 'transition_in_progress');
        }
        if (this.record.transitions.some((t) => t.transitionId === transitionId)) {
            throw new EngineError(`Transition ${transitionId} already completed`, 'transition_replayed');
        }
        this.assertState(['ready', 'round_active', 'checkpoint', 'needs_attention'], 'beginTransition');
        if (!this.record.lastReconciledAt)
            throw new EngineError('Reconcile before advancing', 'not_reconciled');
        if (this.record.lastTransitionAt && this.record.lastReconciledAt < this.record.lastTransitionAt && this.record.state !== 'round_active') {
            throw new EngineError('Reconcile after the last transition before advancing', 'stale_reconciliation');
        }
        const targetRoundId = target === 'end' ? null : (this.targetRound() ?? null);
        if (target === 'next' && !targetRoundId)
            throw new EngineError('No further round', 'no_next_round');
        const plan = target === 'next' ? this.previewNext() : null;
        const t = {
            transitionId,
            targetRoundId,
            startedBy: this.opts.actor,
            startedAt: this.now().toISOString(),
            phase: this.record.state === 'round_active' ? 'closing' : 'checkpoint',
            intended: plan ? [...plan.destinations].map(([participantId, to]) => ({ participantId, to })) : [],
            written: [],
            failed: [],
            mismatched: [],
            acceptedExceptions: false,
        };
        this.record.activeTransition = t;
        this.audit('beginTransition', 'ok', `${transitionId} -> ${targetRoundId ?? 'end'}`);
        return t;
    }
    /** Step 3 of section 10: close the rooms and enter the checkpoint. */
    async closeRooms(transitionId) {
        const t = this.activeTransition(transitionId);
        if (t.phase !== 'closing')
            return; // already past this step
        this.record.state = 'closing';
        try {
            await this.adapter.closeRooms();
        }
        catch (e) {
            this.fail(t, 'closeRooms', e);
            throw e;
        }
        if (this.record.currentRoundId) {
            const rt = (this.record.roundTimes[this.record.currentRoundId] ??= {});
            rt.closedAt = this.now().toISOString();
        }
        this.record.state = 'checkpoint';
        this.record.lastTransitionAt = this.now().toISOString();
        t.phase = 'checkpoint';
        this.audit('closeRooms', 'ok', transitionId);
        if (t.targetRoundId === null) {
            this.finish(t);
            this.record.state = 'completed';
            this.audit('endRotations', 'ok', transitionId);
        }
    }
    /** Step 4: reconcile at the checkpoint and record checkpoint attendance for the round just closed. */
    async checkpoint(transitionId) {
        const t = this.activeTransition(transitionId);
        if (t.phase !== 'checkpoint')
            throw new EngineError(`Transition is in phase ${t.phase}, not checkpoint`, 'wrong_phase');
        const result = await this.reconcile();
        if (this.record.currentRoundId) {
            const roundStatuses = (this.record.statuses[this.record.currentRoundId] ??= {});
            const bound = new Set(this.record.bindings.map((b) => b.participantId));
            for (const p of this.schedule().participants) {
                if (this.record.excluded.includes(p.participantId))
                    continue;
                roundStatuses[p.participantId] = bound.has(p.participantId) ? 'present' : 'absent';
            }
        }
        // Recompute intent with the fresh absent set.
        if (t.targetRoundId) {
            const plan = this.previewNext();
            t.intended = plan ? [...plan.destinations].map(([participantId, to]) => ({ participantId, to })) : [];
        }
        t.phase = 'writing';
        return result;
    }
    /** Step 5: write only the changed assignments, with bounded paced retries, then verify against the listing. */
    async writeAssignments(transitionId) {
        const t = this.activeTransition(transitionId);
        if (t.phase !== 'writing' && t.phase !== 'verifying')
            throw new EngineError(`Transition is in phase ${t.phase}, not writing`, 'wrong_phase');
        if (!t.targetRoundId)
            throw new EngineError('End transition has nothing to write', 'wrong_phase');
        const plan = this.previewNext();
        if (!plan)
            throw new EngineError('No plan for target round', 'no_plan');
        const bindingByPid = new Map(this.record.bindings.map((b) => [b.participantId, b]));
        const roundStatuses = (this.record.statuses[t.targetRoundId] ??= {});
        for (const [pid] of plan.destinations)
            roundStatuses[pid] ??= 'planned';
        const pending = plan.changes.filter((c) => !t.written.includes(c.participantId));
        t.failed = [];
        for (const change of pending) {
            const b = bindingByPid.get(change.participantId);
            if (!b) {
                t.failed.push({ participantId: change.participantId, error: 'no binding' });
                continue;
            }
            const platformRoom = change.to === MAIN ? null : this.record.roomMap[change.to];
            if (change.to !== MAIN && !platformRoom) {
                t.failed.push({ participantId: change.participantId, error: `room ${change.to} not mapped` });
                continue;
            }
            const err = await this.writeWithRetry(b.runtimeId, platformRoom ?? null);
            if (err) {
                t.failed.push({ participantId: change.participantId, error: err });
                roundStatuses[change.participantId] = 'unknown';
            }
            else {
                t.written.push(change.participantId);
                roundStatuses[change.participantId] = 'written';
            }
            if (this.paceMs)
                await this.sleep(this.paceMs);
        }
        t.phase = 'verifying';
        await this.verify(t, roundStatuses);
        this.audit('writeAssignments', t.failed.length || t.mismatched.length ? 'partial' : 'ok', `${t.written.length} written, ${t.failed.length} failed, ${t.mismatched.length} mismatched`);
        if (t.failed.length || t.mismatched.length) {
            this.record.state = 'needs_attention';
        }
        else {
            t.phase = 'opening';
        }
        return t;
    }
    /** FR14: after a partial failure, re-read the listing and retry only what is still wrong. */
    async retryUnresolved(transitionId) {
        const t = this.activeTransition(transitionId);
        if (this.record.state !== 'needs_attention')
            throw new EngineError('Nothing to retry', 'wrong_phase');
        // Treat mismatches as unwritten so they are attempted again.
        const redo = new Set([...t.failed.map((f) => f.participantId), ...t.mismatched.map((m) => m.participantId)]);
        t.written = t.written.filter((pid) => !redo.has(pid));
        t.phase = 'writing';
        this.record.state = 'checkpoint';
        return this.writeAssignments(transitionId);
    }
    /** Operator decision to open with named exceptions (section 10, step 6). */
    acceptExceptions(transitionId) {
        const t = this.activeTransition(transitionId);
        if (this.record.state !== 'needs_attention')
            throw new EngineError('No exceptions to accept', 'wrong_phase');
        t.acceptedExceptions = true;
        t.phase = 'opening';
        this.record.state = 'checkpoint';
        this.audit('acceptExceptions', 'ok', `${t.failed.length} failed, ${t.mismatched.length} mismatched`);
    }
    /** Step 6: open the rooms and start the round. */
    async openRooms(transitionId) {
        const t = this.activeTransition(transitionId);
        if (t.phase !== 'opening')
            throw new EngineError(`Transition is in phase ${t.phase}, not opening`, 'wrong_phase');
        if (!t.targetRoundId)
            throw new EngineError('End transition does not open rooms', 'wrong_phase');
        this.record.state = 'opening';
        try {
            await this.adapter.openRooms();
        }
        catch (e) {
            this.fail(t, 'openRooms', e);
            throw e;
        }
        this.record.currentRoundId = t.targetRoundId;
        (this.record.roundTimes[t.targetRoundId] ??= {}).openedAt = this.now().toISOString();
        await this.refreshRuntimeIdsFromListing();
        this.record.lastTransitionAt = this.now().toISOString();
        this.record.state = 'round_active';
        this.finish(t);
        this.audit('openRooms', 'ok', `${transitionId} round ${t.targetRoundId}`);
    }
    /** Convenience: run the whole sequence; stops in needs_attention on any problem. */
    async advance(transitionId) {
        const t = this.beginTransition(transitionId, 'next');
        if (t.phase === 'closing')
            await this.closeRooms(transitionId);
        if (t.phase === 'checkpoint')
            await this.checkpoint(transitionId);
        if (t.phase === 'writing')
            await this.writeAssignments(transitionId);
        if (t.phase === 'opening')
            await this.openRooms(transitionId);
        return this.record.transitions.find((x) => x.transitionId === transitionId) ?? t;
    }
    async endRotations(transitionId) {
        const t = this.beginTransition(transitionId, 'end');
        if (this.record.state === 'round_active' || t.phase === 'closing') {
            t.phase = 'closing';
            await this.closeRooms(transitionId);
        }
        else {
            this.finish(t);
            this.record.state = 'completed';
            this.audit('endRotations', 'ok', transitionId);
        }
    }
    // ---- observation ------------------------------------------------------
    /** FR15: compare the platform listing with the intended assignments for the current round. */
    async detectDiscrepancies() {
        const round = this.record.currentRoundId;
        if (!round)
            return [];
        const listing = await this.adapter.listRooms();
        return this.compare(this.destinationsFor(round), listing);
    }
    recordManualObservation(participantId, roomId, note) {
        const roundId = this.record.currentRoundId;
        if (!roundId)
            throw new EngineError('No active round', 'no_round');
        const o = { at: this.now().toISOString(), by: this.opts.actor, participantId, roundId, roomId };
        if (note)
            o.note = note;
        this.record.manualObservations.push(o);
        this.audit('manualObservation', 'ok', `${participantId} in ${roomId}`);
    }
    /** Called when the adapter reports a room change the controller did not command. */
    noteUncommandedChange() {
        if (this.record.activeTransition)
            return; // we are mid-transition; expected
        for (const b of this.record.bindings)
            b.stale = true;
        this.audit('uncommandedChange', 'ok', 'bindings marked stale');
    }
    // ---- internals --------------------------------------------------------
    schedule() {
        const v = this.record.scheduleVersion;
        if (!v)
            throw new EngineError('No schedule imported', 'no_schedule');
        return v.schedule;
    }
    targetRound() {
        const s = this.schedule();
        if (this.record.state === 'ready' || this.record.currentRoundId === null)
            return roundsInOrder(s)[0]?.roundId;
        return nextRound(s, this.record.currentRoundId)?.roundId;
    }
    destinationsFor(roundId) {
        const plan = computeRoundPlan({
            schedule: this.schedule(),
            roundId,
            previousRoundId: null,
            overrides: this.record.overrides,
            absent: new Set(this.record.absent),
            excluded: new Set(this.record.excluded),
        });
        return plan.destinations;
    }
    activeTransition(transitionId) {
        const t = this.record.activeTransition;
        if (!t)
            throw new EngineError('No transition in progress', 'no_transition');
        if (t.transitionId !== transitionId)
            throw new EngineError(`Transition ${t.transitionId} is in progress, not ${transitionId}`, 'transition_mismatch');
        return t;
    }
    assertState(allowed, action) {
        if (!allowed.includes(this.record.state))
            throw new EngineError(`Cannot ${action} in state ${this.record.state}`, 'wrong_state');
    }
    async writeWithRetry(runtimeId, platformRoom) {
        let last = 'unknown error';
        for (let attempt = 1; attempt <= this.maxWriteAttempts; attempt++) {
            try {
                await this.adapter.assign(runtimeId, platformRoom);
                return null;
            }
            catch (e) {
                last = e instanceof Error ? e.message : String(e);
                const retryable = e instanceof PlatformError ? e.retryable : true;
                if (!retryable)
                    return last;
                if (attempt < this.maxWriteAttempts && this.paceMs)
                    await this.sleep(this.paceMs * attempt);
            }
        }
        return last;
    }
    async verify(t, roundStatuses) {
        if (!t.targetRoundId)
            return;
        let listing;
        try {
            listing = await this.adapter.listRooms();
        }
        catch {
            t.mismatched = [];
            return; // cannot verify; statuses stay at 'written'
        }
        const intended = new Map(t.intended.map((i) => [i.participantId, i.to]));
        t.mismatched = this.compare(intended, listing).filter((d) => !t.failed.some((f) => f.participantId === d.participantId));
        for (const [pid] of intended) {
            if (t.mismatched.some((m) => m.participantId === pid) || t.failed.some((f) => f.participantId === pid))
                continue;
            roundStatuses[pid] = 'listed';
        }
    }
    compare(intended, listing) {
        const platformToLogical = new Map(Object.entries(this.record.roomMap).map(([logical, platform]) => [platform, logical]));
        const runtimeToRoom = new Map();
        const persistentToRoom = new Map();
        for (const room of listing.rooms) {
            const logical = platformToLogical.get(room.platformRoomId);
            if (!logical)
                continue;
            for (const m of room.assigned) {
                runtimeToRoom.set(m.runtimeId, logical);
                if (m.persistentId)
                    persistentToRoom.set(m.persistentId, logical);
            }
        }
        const bindingByPid = new Map(this.record.bindings.map((b) => [b.participantId, b]));
        const out = [];
        for (const [pid, want] of intended) {
            const b = bindingByPid.get(pid);
            if (!b)
                continue;
            const listed = runtimeToRoom.get(b.runtimeId) ?? (b.persistentId ? persistentToRoom.get(b.persistentId) : undefined) ?? MAIN;
            if (listed !== want)
                out.push({ participantId: pid, intended: want, listed });
        }
        return out;
    }
    /**
     * Opening or closing rooms changes runtime identifiers on Zoom. Where the
     * room listing carries the persistent identifier, bring bindings up to date
     * so that in-round actions (late placement, discrepancy checks) address the
     * current identifier. Bindings without a persistent id are left as they are
     * and will be rebuilt at the next checkpoint.
     */
    async refreshRuntimeIdsFromListing() {
        let listing;
        try {
            listing = await this.adapter.listRooms();
        }
        catch {
            return;
        }
        const byPersistent = new Map();
        for (const room of listing.rooms)
            for (const m of room.assigned)
                if (m.persistentId)
                    byPersistent.set(m.persistentId, m.runtimeId);
        for (const b of this.record.bindings) {
            const fresh = b.persistentId ? byPersistent.get(b.persistentId) : undefined;
            if (fresh)
                b.runtimeId = fresh;
        }
    }
    fail(t, step, e) {
        t.phase = 'failed';
        this.record.state = 'needs_attention';
        this.audit(step, 'failed', e instanceof Error ? e.message : String(e));
    }
    finish(t) {
        t.phase = 'done';
        t.completedAt = this.now().toISOString();
        this.record.transitions.push(t);
        this.record.activeTransition = null;
    }
    audit(action, result, detail) {
        const entry = { at: this.now().toISOString(), actor: this.opts.actor, scheduleVersion: this.record.scheduleVersion?.version ?? null, action, result };
        if (detail)
            entry.detail = detail;
        this.record.audit.push(entry);
    }
}
//# sourceMappingURL=engine.js.map